import { api } from "@/shared/api";

export interface DepartmentNode {
  id: number;
  name: string;
  parentId: number; // 0 = 根
  path: string;
  depth: number;
  sort: number;
  source: "WECOM" | "MANUAL";
  enabled: boolean;
}

export interface OrgMember {
  principalId: number;
  displayName: string;
  position: "MEMBER" | "MANAGER";
  primary: boolean;
  /** 2026-07-08 双源并存:MANUAL=手工接管(可移除/可恢复企微托管),WECOM=企微托管(不可手动移除)。 */
  source: "WECOM" | "MANUAL";
}

export interface CreateDepartmentParams {
  name: string;
  parentId: number;
  sort?: number;
}
export interface UpdateDepartmentParams {
  name?: string;
  sort?: number;
  enabled?: boolean;
}

/** 全租户成员一行(组织筛选人名搜索):同人多归属=多行,departmentId 供 selectMember(dept, member) 二元组。 */
export interface OrgMemberWithDept {
  principalId: number;
  displayName: string;
  position: "MEMBER" | "MANAGER";
  departmentId: number;
}

/**
 * 2026-09-16 — 一条**待确认**的负责人建议(企微说这人是部门负责人,我们这边还没点头)。
 *
 * 在确认之前他**不是**经理:role 派生只认已确认的行。带人名和部门名,是因为管理员要处理的是
 * 「张三是不是销售部负责人」,给一对 id 让他自己去别的页面查,这个待办就没人会处理。
 */
export interface PendingManager {
  principalId: number;
  displayName: string;
  departmentId: number;
  departmentName: string;
}

export const orgService = {
  listDepartments: () => api.get<DepartmentNode[]>("/tenant/departments"),
  createDepartment: (data: CreateDepartmentParams) =>
    api.post<DepartmentNode>("/tenant/departments", data),
  updateDepartment: (id: number, data: UpdateDepartmentParams) =>
    api.put<void>(`/tenant/departments/${id}`, data),
  moveDepartment: (id: number, newParentId: number) =>
    api.put<void>(`/tenant/departments/${id}/move`, { newParentId }),
  deleteDepartment: (id: number) => api.delete<void>(`/tenant/departments/${id}`),

  listMembers: (departmentId: number) =>
    api.get<OrgMember[]>(`/tenant/org-memberships/by-department/${departmentId}`),
  /** 2026-07-13 组织筛选人名搜索:全员平铺(带 departmentId),一次拉平前端缓存做名字匹配。 */
  listAllMembers: () => api.get<OrgMemberWithDept[]>("/tenant/org-memberships/all"),
  setMembership: (principalId: number, departmentId: number, primary: boolean) =>
    api.post<void>("/tenant/org-memberships", { principalId, departmentId, primary }),
  /** 2026-09-16 — 企微同步来的、待管理员确认的负责人建议。 */
  listPendingManagers: () => api.get<PendingManager[]>("/tenant/org-memberships/pending-managers"),

  /** 确认(approve=true)/ 驳回一条负责人建议。确认后该成员下次登录即为经理。 */
  resolveManager: (principalId: number, departmentId: number, approve: boolean) =>
    api.put<void>(
      `/tenant/org-memberships/manager/resolve?principalId=${principalId}&departmentId=${departmentId}&approve=${approve}`,
      null
    ),

  setManager: (principalId: number, departmentId: number, manager: boolean) =>
    api.put<void>("/tenant/org-memberships/manager", { principalId, departmentId, manager }),
  removeMembership: (principalId: number, departmentId: number) =>
    api.delete<void>(
      `/tenant/org-memberships?principalId=${principalId}&departmentId=${departmentId}`
    ),
  /** 2026-07-08 恢复企微托管:把手工接管(MANUAL)的归属行翻回 WECOM,下次同步按通讯录对账。 */
  restoreWecomMembership: (principalId: number, departmentId: number) =>
    api.put<void>(
      `/tenant/org-memberships/restore-wecom?principalId=${principalId}&departmentId=${departmentId}`,
      {}
    ),

  /**
   * 治理清债:撤掉根部门全部手工负责人行(走事件踢会话,role 重派生)。返回 {cleaned}。
   * 「全员设为负责人」批量入口已下线(把每个人的管理子树放大成全租户,是 K6/F5 视野放大的同源根因);
   * 后端 promote-all 端点保留仅作脚本用,前端不再露出。
   */
  demoteRootManagers: () => api.post<{ cleaned: number }>("/tenant/org/demote-root-managers", {}),
};
